import { useAssets, useCashflows, useProfile, useScenarios } from '../../hooks/useApi';
import { useExchangeRates } from '../../hooks/useExchangeRates';
import { useNavigate } from 'react-router-dom';
import { formatAmount } from '../../utils/numberFormat';
import { DynamicCashFlow, Currency, Country } from '../../types/api.types';
import { OnboardingProgress } from '../../components/OnboardingProgress/OnboardingProgress';
import styles from './DashboardPage.module.scss';

const DEFAULT_LIFE_EXPECTANCY: Record<Country, number> = { INDIA: 80, US: 83, CAN: 84 };

function getRetirementExpenses(cashflows: DynamicCashFlow[], defaultScenarioId?: string): DynamicCashFlow[] {
    const all = cashflows.filter((cf) => cf.type === 'EXPENSE' && cf.isRetirementExpense);
    if (!defaultScenarioId) return all;
    return all.filter((cf) => !cf.retirementScenarioId || cf.retirementScenarioId === defaultScenarioId);
}

function convertCurrency(
    amount: number,
    fromCurrency: Currency,
    toCurrency: Currency,
    rates: Record<Currency, number>,
): number {
    if (fromCurrency === toCurrency) return amount;
    return (amount / rates[fromCurrency]) * rates[toCurrency];
}

function getFireMultiplier(retirementYears: number): number {
    if (retirementYears <= 30) return 25;
    if (retirementYears <= 35) return 28;
    if (retirementYears <= 40) return 30;
    if (retirementYears <= 45) return 32;
    return 33;
}

function computeFireMetrics(
    assets: { balance: number; expectedReturn: number }[],
    cashflows: DynamicCashFlow[],
    currentAge: number,
    retirementAge: number,
    baseCurrency: Currency,
    rates: Record<Currency, number>,
    retirementCountry: Country,
    defaultScenarioId?: string,
) {
    const totalAssets = assets.reduce((sum, a) => sum + a.balance, 0);
    const avgReturn =
        totalAssets > 0 ? assets.reduce((sum, a) => sum + a.expectedReturn * a.balance, 0) / totalAssets / 100 : 0.07;

    const yearsToRetirement = Math.max(0, retirementAge - currentAge);
    const lifeExpectancy = DEFAULT_LIFE_EXPECTANCY[retirementCountry] || 83;
    const retirementYears = lifeExpectancy - retirementAge;
    const fireMultiplier = getFireMultiplier(retirementYears);

    const retirementExpenses = getRetirementExpenses(cashflows, defaultScenarioId);
    const recurringExpenses = retirementExpenses.filter((cf) => !cf.isOneTime);
    const oneTimeExpenses = retirementExpenses.filter((cf) => cf.isOneTime);

    // Retirement expense amounts are entered in retirement-year purchasing power (isRetirementExpense=true),
    // so use cf.amount directly — no pre-retirement inflation adjustment.
    const monthlyRetirementExpense = recurringExpenses.reduce((sum, cf) => {
        const convertedAmount = convertCurrency(cf.amount, cf.currency, baseCurrency, rates);
        return sum + convertedAmount;
    }, 0);
    const annualRetirementExpense = monthlyRetirementExpense * 12;
    const oneTimeTotal = oneTimeExpenses.reduce((sum, cf) => {
        const convertedAmount = convertCurrency(cf.amount, cf.currency, baseCurrency, rates);
        return sum + convertedAmount;
    }, 0);
    const fireNumber = annualRetirementExpense * fireMultiplier + oneTimeTotal;

    const activeIncome = cashflows.filter(
        (cf) => cf.type === 'INCOME' && currentAge >= cf.startAge && currentAge <= cf.endAge,
    );
    const activeExpenses = cashflows.filter(
        (cf) => cf.type === 'EXPENSE' && currentAge >= cf.startAge && currentAge <= cf.endAge,
    );
    const monthlyIncome = activeIncome.reduce(
        (sum, cf) => sum + convertCurrency(cf.amount, cf.currency, baseCurrency, rates),
        0,
    );
    const monthlyExpenses = activeExpenses.reduce(
        (sum, cf) => sum + convertCurrency(cf.amount, cf.currency, baseCurrency, rates),
        0,
    );
    const annualSavings = (monthlyIncome - monthlyExpenses) * 12;
    const savingsRate = monthlyIncome > 0 ? ((monthlyIncome - monthlyExpenses) / monthlyIncome) * 100 : 0;

    const progress = fireNumber > 0 ? Math.min((totalAssets / fireNumber) * 100, 100) : 0;

    let yearsRemaining = Infinity;
    if (fireNumber > 0 && annualSavings > 0 && avgReturn > 0) {
        const target = fireNumber;
        const r = avgReturn;
        const s = annualSavings;
        const p = totalAssets;
        if (p >= target) {
            yearsRemaining = 0;
        } else {
            yearsRemaining = Math.log((target * r + s) / (p * r + s)) / Math.log(1 + r);
            yearsRemaining = Math.max(0, Math.ceil(yearsRemaining));
        }
    } else if (fireNumber > 0 && totalAssets >= fireNumber) {
        yearsRemaining = 0;
    }

    let coastFireAge = Infinity;
    if (fireNumber > 0 && totalAssets > 0 && avgReturn > 0) {
        const yearsToCoast = Math.log(fireNumber / totalAssets) / Math.log(1 + avgReturn);
        coastFireAge = Math.ceil(currentAge + yearsToCoast);
    }

    let status: 'ahead' | 'on_track' | 'behind' = 'on_track';
    if (yearsRemaining === 0 || yearsRemaining < yearsToRetirement - 2) {
        status = 'ahead';
    } else if (yearsRemaining > yearsToRetirement) {
        status = 'behind';
    }

    return {
        totalAssets,
        fireNumber,
        fireMultiplier,
        retirementYears,
        lifeExpectancy,
        annualRetirementExpense,
        oneTimeTotal,
        monthlyRetirementExpense,
        progress,
        yearsRemaining,
        savingsRate,
        coastFireAge,
        status,
        yearsToRetirement,
        hasExpenses: retirementExpenses.length > 0,
        hasAssets: assets.length > 0,
    };
}

const COUNTRY_LABELS: Record<string, string> = {
    INDIA: 'India',
    US: 'United States',
    CAN: 'Canada',
};

const COUNTRY_ACCENT: Record<string, string> = {
    INDIA: '#FF9933',
    US: '#B22234',
    CAN: '#D52B1E',
};

const DESTINATION_GRADIENT: Record<string, string> = {
    INDIA: 'linear-gradient(160deg, #002366 0%, #1a40c2 45%, #FF9933 100%)',
    US: 'linear-gradient(160deg, #002366 0%, #1a40c2 55%, #B22234 100%)',
    CAN: 'linear-gradient(160deg, #002366 0%, #1a40c2 55%, #D52B1E 100%)',
};

const STATUS_LABELS: Record<string, string> = {
    ahead: 'Ahead of Schedule',
    on_track: 'On Track',
    behind: 'Behind Schedule',
};

const STATUS_CLASS: Record<string, string> = {
    ahead: styles.statusAhead,
    on_track: styles.statusOnTrack,
    behind: styles.statusBehind,
};

export function DashboardPage() {
    const { assets, loading: assetsLoading } = useAssets();
    const { cashflows, loading: cashflowsLoading } = useCashflows();
    const { profile, loading: profileLoading } = useProfile();
    const { scenarios } = useScenarios();
    const { rates, loading: ratesLoading } = useExchangeRates();
    const navigate = useNavigate();

    const profileRetirementAge = profile?.targetRetirementAge ?? 60;
    const currentAge = profile?.currentAge ?? 35;
    const baseCurrency = profile?.baseCurrency ?? 'USD';

    const loading = assetsLoading || cashflowsLoading || profileLoading || ratesLoading;

    if (loading) {
        return (
            <div className={styles.page}>
                <p className={styles.loading}>Loading your dashboard…</p>
            </div>
        );
    }

    const defaultScenario = scenarios.find((s) => s.isDefault);

    // Prefer the default scenario's explicitly chosen country; fall back to residency history inference
    const retirementCountry: Country = (() => {
        if (defaultScenario?.country) return defaultScenario.country;
        const history = profile?.residencyHistory ?? [];
        const atRetirement = history.find(
            (r) => profileRetirementAge >= r.startAge && profileRetirementAge <= r.endAge,
        );
        if (atRetirement) return atRetirement.country;
        const sorted = [...history].sort((a, b) => b.endAge - a.endAge);
        return sorted[0]?.country ?? 'US';
    })();
    const metrics = computeFireMetrics(
        assets,
        cashflows,
        currentAge,
        profileRetirementAge,
        baseCurrency,
        rates,
        retirementCountry,
        defaultScenario?.scenarioId,
    );

    const assetTotal = assets.reduce((sum, a) => sum + convertCurrency(a.balance, a.currency, baseCurrency, rates), 0);
    const byCountry = assets.reduce<Record<string, number>>((acc, a) => {
        const converted = convertCurrency(a.balance, a.currency, baseCurrency, rates);
        acc[a.country] = (acc[a.country] ?? 0) + converted;
        return acc;
    }, {});

    const countryEntries = Object.entries(byCountry)
        .map(([country, amount]) => ({
            country,
            amount,
            pct: assetTotal > 0 ? (amount / assetTotal) * 100 : 0,
        }))
        .sort((a, b) => b.amount - a.amount);

    const yearsToRetire = Math.max(0, profileRetirementAge - currentAge);
    const yearsToCoast =
        metrics.coastFireAge !== Infinity && metrics.coastFireAge > currentAge
            ? metrics.coastFireAge - currentAge
            : null;

    const destinationGradient = DESTINATION_GRADIENT[retirementCountry] ?? DESTINATION_GRADIENT['US'];

    const milestones = [
        yearsToCoast !== null
            ? {
                  emoji: '⚓',
                  heading: `Age ${metrics.coastFireAge}`,
                  label: 'Coast FIRE',
                  countdown: `${yearsToCoast} yr${yearsToCoast !== 1 ? 's' : ''} to go`,
                  active: true,
              }
            : {
                  emoji: '🎯',
                  heading: `${metrics.progress.toFixed(0)}% there`,
                  label: 'FIRE Progress',
                  countdown: 'Keep investing',
                  active: false,
              },
        {
            emoji: '✈️',
            heading: `Age ${profileRetirementAge}`,
            label: 'Target Retirement',
            countdown:
                yearsToRetire === 0
                    ? 'You can retire now!'
                    : `${yearsToRetire} yr${yearsToRetire !== 1 ? 's' : ''} to go`,
            active: yearsToRetire <= 5 && yearsToRetire > 0,
        },
        {
            emoji: '🌅',
            heading: COUNTRY_LABELS[retirementCountry] ?? retirementCountry,
            label: 'Retirement Life',
            countdown: `Life expectancy: ${metrics.lifeExpectancy}`,
            active: false,
        },
    ];

    return (
        <div className={styles.page}>
            <section className={styles.pageHeader}>
                <h1 className={styles.title}>Global Command Center</h1>
                <p className={styles.subtitle}>Your cross-border wealth, unified and projected.</p>
            </section>

            <OnboardingProgress />

            <div className={styles.bentoGrid}>
                {/* ── Hero Banner ── */}
                <div className={styles.heroBanner}>
                    <div className={styles.heroLeft}>
                        <div className={styles.netWorthBlock}>
                            <span className={styles.netWorthLabel}>Unified Net Worth</span>
                            <span className={styles.netWorthFigure}>
                                {assetTotal > 0 ? formatAmount(Math.round(assetTotal), baseCurrency) : '—'}
                            </span>
                        </div>

                        {metrics.hasExpenses ? (
                            <div className={styles.fireGoalBlock}>
                                <div className={styles.fireGoalTop}>
                                    <div>
                                        <span className={styles.fireGoalLabel}>
                                            FIRE GOAL
                                            {defaultScenario ? ` — ${defaultScenario.name}` : ''}
                                        </span>
                                        <span className={styles.fireGoalAmount}>
                                            {formatAmount(Math.round(metrics.fireNumber), baseCurrency)}
                                        </span>
                                    </div>
                                    <div className={styles.fireGoalRight}>
                                        <span className={`${styles.statusBadge} ${STATUS_CLASS[metrics.status] ?? ''}`}>
                                            {STATUS_LABELS[metrics.status]}
                                        </span>
                                        <span className={styles.progressPct}>
                                            {metrics.progress.toFixed(0)}% Achieved
                                        </span>
                                    </div>
                                </div>
                                <div className={styles.progressTrack}>
                                    <div className={styles.progressFill} style={{ width: `${metrics.progress}%` }} />
                                </div>
                                <p className={styles.fireExplanation}>
                                    Based on {metrics.retirementYears}-year retirement horizon (retire at{' '}
                                    {profileRetirementAge}, life expectancy {metrics.lifeExpectancy}). Multiplier ×
                                    {metrics.fireMultiplier} = {(100 / metrics.fireMultiplier).toFixed(1)}% safe
                                    withdrawal rate.
                                </p>
                            </div>
                        ) : (
                            <div className={styles.fireEmptyBlock}>
                                <p>Add your retirement budget to calculate your FIRE number.</p>
                                <button
                                    className={styles.fireEmptyBtn}
                                    onClick={() => navigate('/cashflows?tab=RETIREMENT')}
                                >
                                    + Set Up Retirement Budget
                                </button>
                            </div>
                        )}
                    </div>

                    <div className={styles.heroRight}>
                        <div className={styles.destinationCard} style={{ background: destinationGradient }}>
                            <div className={styles.destinationOverlay}>
                                <span className={styles.destinationSub}>RETIREMENT DESTINATION</span>
                                <span className={styles.destinationName}>
                                    {COUNTRY_LABELS[retirementCountry] ?? retirementCountry}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Asset Allocation ── */}
                <div className={styles.assetAllocationCard}>
                    <div className={styles.cardHeader}>
                        <h3 className={styles.cardTitle}>Asset Allocation</h3>
                        <button className={styles.cardLink} onClick={() => navigate('/assets')}>
                            Manage →
                        </button>
                    </div>

                    {countryEntries.length > 0 ? (
                        <div className={styles.allocationGrid}>
                            {countryEntries.map(({ country, amount, pct }) => (
                                <div
                                    key={country}
                                    className={styles.countryBlock}
                                    style={{ borderLeftColor: COUNTRY_ACCENT[country] ?? '#747686' }}
                                >
                                    <div className={styles.countryHeader}>
                                        <span
                                            className={styles.countryDot}
                                            style={{ background: COUNTRY_ACCENT[country] ?? '#747686' }}
                                        />
                                        <span className={styles.countryLabel}>
                                            {COUNTRY_LABELS[country] ?? country}
                                        </span>
                                        <span className={styles.countryPct}>{pct.toFixed(0)}%</span>
                                    </div>
                                    <span className={styles.countryAmount}>
                                        {formatAmount(Math.round(amount), baseCurrency)}
                                    </span>
                                    <div className={styles.allocationBar}>
                                        <div
                                            className={styles.allocationBarFill}
                                            style={{
                                                width: `${pct}%`,
                                                background: COUNTRY_ACCENT[country] ?? '#3b5bdb',
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className={styles.emptyState}>
                            <p>No assets added yet.</p>
                            <button className={styles.emptyStateBtn} onClick={() => navigate('/assets')}>
                                + Add Your First Asset
                            </button>
                        </div>
                    )}
                </div>

                {/* ── Strategic Insights ── */}
                <div className={styles.insightsCard}>
                    <h3 className={styles.cardTitle}>Strategic Insights</h3>
                    <div className={styles.insightsList}>
                        <button
                            className={`${styles.insightItem} ${styles.insightWarning}`}
                            onClick={() => navigate('/tax')}
                        >
                            <span className={styles.insightIcon}>💰</span>
                            <div className={styles.insightContent}>
                                <span className={styles.insightTitle}>Tax Optimization</span>
                                <span className={styles.insightDesc}>
                                    Review DTAA treaty benefits and retirement account contribution limits.
                                </span>
                            </div>
                        </button>
                        <button
                            className={`${styles.insightItem} ${styles.insightGain}`}
                            onClick={() => navigate('/assets')}
                        >
                            <span className={styles.insightIcon}>📈</span>
                            <div className={styles.insightContent}>
                                <span className={styles.insightTitle}>Portfolio Rebalancing</span>
                                <span className={styles.insightDesc}>
                                    {countryEntries.length > 0
                                        ? `Assets across ${countryEntries.length} countr${countryEntries.length !== 1 ? 'ies' : 'y'}. Review target allocation.`
                                        : 'Add assets to get rebalancing recommendations.'}
                                </span>
                            </div>
                        </button>
                        <button
                            className={`${styles.insightItem} ${styles.insightPrimary}`}
                            onClick={() => navigate('/simulator')}
                        >
                            <span className={styles.insightIcon}>🔭</span>
                            <div className={styles.insightContent}>
                                <span className={styles.insightTitle}>Run Monte Carlo Projection</span>
                                <span className={styles.insightDesc}>
                                    {metrics.hasExpenses
                                        ? `${metrics.progress.toFixed(0)}% to FIRE goal — simulate different scenarios.`
                                        : 'Set up your cashflows to unlock projections.'}
                                </span>
                            </div>
                        </button>
                    </div>
                </div>

                {/* ── Upcoming Milestones ── */}
                <div className={styles.milestonesCard}>
                    <h3 className={styles.cardTitle}>Upcoming Milestones</h3>
                    <div className={styles.timeline}>
                        {milestones.map((m, i) => (
                            <div key={i} className={styles.timelineItem}>
                                <div
                                    className={`${styles.milestoneCircle} ${m.active ? styles.milestoneActive : styles.milestoneInactive}`}
                                >
                                    {m.emoji}
                                </div>
                                <div className={styles.milestoneInfo}>
                                    <span className={styles.milestoneHeading}>{m.heading}</span>
                                    <span className={styles.milestoneLabel}>{m.label}</span>
                                    <span
                                        className={`${styles.milestoneCountdown} ${m.active ? styles.milestoneCountdownActive : ''}`}
                                    >
                                        {m.countdown}
                                    </span>
                                </div>
                                {i < milestones.length - 1 && <div className={styles.milestoneConnector} />}
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Quick Actions ── */}
                <div className={styles.quickActionsCard}>
                    <h3 className={styles.cardTitle}>Quick Actions</h3>
                    <div className={styles.actionsList}>
                        <button
                            className={`${styles.actionBtn} ${styles.actionBtnPrimary}`}
                            onClick={() => navigate('/simulator')}
                        >
                            <div className={styles.actionBtnLeft}>
                                <span>📊</span>
                                <span>Run Simulation</span>
                            </div>
                            <span>›</span>
                        </button>
                        <button
                            className={`${styles.actionBtn} ${styles.actionBtnSecondary}`}
                            onClick={() => navigate('/assets')}
                        >
                            <div className={styles.actionBtnLeft}>
                                <span>➕</span>
                                <span>Add New Asset</span>
                            </div>
                            <span>›</span>
                        </button>
                        <button
                            className={`${styles.actionBtn} ${styles.actionBtnSecondary}`}
                            onClick={() => navigate('/tax')}
                        >
                            <div className={styles.actionBtnLeft}>
                                <span>🧾</span>
                                <span>Tax Deep-Dive</span>
                            </div>
                            <span>›</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
